import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useBulkUpdateWorkOrderPriority } from '@/features/work-orders/queries';
import type { WorkOrderPriority } from '@/features/work-orders/types';

const PRIORITIES: WorkOrderPriority[] = ['low', 'normal', 'high', 'critical'];

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm';

/**
 * L16 — bulk priority change confirmation modal.
 *
 * Shows the count of affected WOs + a priority dropdown. Submitting
 * fires the mutation and reports `failedIds` if any (signed/closed
 * WOs that the backend skipped).
 */
export function BulkPriorityModal({
  open,
  onOpenChange,
  ids,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ids: string[];
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [priority, setPriority] = useState<WorkOrderPriority>('normal');
  const [result, setResult] = useState<{
    updatedIds: string[];
    failedIds: string[];
  } | null>(null);
  const mut = useBulkUpdateWorkOrderPriority();

  const onConfirm = () => {
    setResult(null);
    mut.mutate(
      { ids, priority },
      {
        onSuccess: (out) => {
          setResult(out);
          if (out.failedIds.length === 0) {
            onSuccess();
            onOpenChange(false);
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mut.reset();
          setResult(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('bulk.priority.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t('bulk.priority.description', { count: ids.length })}
          </p>
          <div className="space-y-1">
            <Label htmlFor="bulk-priority">
              {t('bulk.priority.fieldLabel')}
            </Label>
            <select
              id="bulk-priority"
              className={SELECT_CLASS}
              value={priority}
              onChange={(e) =>
                setPriority(e.target.value as WorkOrderPriority)
              }
              data-testid="bulk-priority-select"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`workOrder.priority.${p}`)}
                </option>
              ))}
            </select>
          </div>
          {result && result.failedIds.length > 0 && (
            <p
              className="text-sm text-amber-700"
              data-testid="bulk-priority-partial"
            >
              {t('bulk.priority.partial', {
                updated: result.updatedIds.length,
                failed: result.failedIds.length,
              })}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mut.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={mut.isPending}
            data-testid="bulk-priority-confirm"
          >
            {t('bulk.priority.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
